import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTime } from '../../utils/datetime';
import { formatNumber, formatWon } from '../../utils/number';
import { FamilyAvatar } from '../../components/FamilyAvatar';

interface ProposalContext {
 proposal_type?: 1 | 2 | 3;
 media_family_hash?: string;
 advertiser_family_hash?: string;
 recipient_family_hash?: string;
 sender_barter_family_hash?: string;
 recipient_barter_family_hash?: string;
}

interface TxRow {
 transaction_hash: string;
 proposal_hash: string;
 payer_mopeer: string;
 payee_mopeer: string;
 status: string;
 mo_family_hash: string | null;
 file_hash: string | null;
 period_start: string;
 period_end: string;
 unit_price: string;
 commission_rate: string;
 payer_confirmed: boolean;
 payee_confirmed: boolean;
 settled_at: string | null;
 closure_reason: string | null;
 created_at: string;
 // joined
 impressions: number | null;
 gross_amount: string | null;
 fee_amount: string | null;
 net_amount: string | null;
 payer_name: string | null;
 payer_email: string | null;
 payer_profile_image: string | null;
 payee_name: string | null;
 payee_email: string | null;
 payee_profile_image: string | null;
 family_name: string | null;
 family_image_url: string | null;
 family_type: string | null;
 family_owner_hash: string | null;
 proposal_context: ProposalContext | null;
 file_filename: string | null;
 campaign_name: string | null;
}

interface FamilyInfo {
 mo_family_hash: string;
 name: string;
 image_url: string | null;
 family_type: string | null;
 owner_mo_peer_hash: string;
 owner_name: string | null;
 owner_email: string | null;
 owner_profile_image: string | null;
}

const STATUS_LABEL: Record<string, string> = {
 contracted: '계약됨', active: '진행 중', both_confirmed: '합의 대기',
 settled: '정산 완료', disputed: '분쟁 중', closed: '종료',
};

// 수수료율 미정 — 확정 전까지 거래 내역에서 수수료/수령 표시 숨김. 확정 시 true 로.
const SHOW_FEE = false;

/** 한 사람 (회원) 표시 — 아바타 + 이름 + 역할 */
const PeerCell: React.FC<{
 hash: string;
 name: string | null;
 email: string | null;
 profileImage: string | null;
 roleLabel: string;
 isMe?: boolean;
}> = ({ hash, name, email, profileImage, roleLabel, isMe }) => (
 <div className="flex items-center gap-2 min-w-0">
  <FamilyAvatar
   name={name || email || hash}
   familyHash={hash}
   imageUrl={profileImage}
   size={20}
  />
  <div className="min-w-0 text-xs">
   <div className="text-gray-500">{roleLabel}{isMe && ' (나)'}</div>
   <div className="font-medium truncate">{name || email || '-'}</div>
  </div>
 </div>
);

/** 한 모패밀리 표시 — 아바타 + 이름 + owner 정보 */
const FamilyCell: React.FC<{
 family: FamilyInfo | undefined;
 fallbackHash: string | null;
 roleLabel: string;
}> = ({ family, fallbackHash, roleLabel }) => {
 if (!family) {
  if (!fallbackHash) return null;
  return (
   <div className="text-xs text-gray-400">
    <div className="text-gray-500">{roleLabel}</div>
    <div className="font-mono truncate" title={fallbackHash}>{fallbackHash.slice(0, 12)}…</div>
   </div>
  );
 }
 return (
  <div className="flex items-center gap-2 min-w-0">
   <FamilyAvatar
    name={family.name}
    familyHash={family.mo_family_hash}
    imageUrl={family.image_url}
    size={36}
   />
   <div className="min-w-0 text-xs">
    <div className="text-gray-500">
     {roleLabel}
     {family.family_type === 'barter' && (
      <span className="ml-1 text-purple-600">🔄</span>
     )}
    </div>
    <div className="font-medium truncate">{family.name}</div>
    {family.owner_name && (
     <div className="text-[10px] text-gray-500 truncate">소유: {family.owner_name}</div>
    )}
   </div>
  </div>
 );
};

const PAGE_SIZE = 20;

const Transactions: React.FC = () => {
 const [rows, setRows] = useState<TxRow[]>([]);
 const [familyMap, setFamilyMap] = useState<Record<string, FamilyInfo>>({});
 const [me, setMe] = useState<string | null>(null);
 const [loading, setLoading] = useState(false);

 // 검색·기간 — 입력 중 (draft) 와 적용된 (applied) 분리. 적용된 값만 fetch 트리거.
 const [queryDraft, setQueryDraft] = useState('');
 const [periodFromDraft, setPeriodFromDraft] = useState('');
 const [periodToDraft, setPeriodToDraft] = useState('');
 const [appliedQuery, setAppliedQuery] = useState('');
 const [appliedFrom, setAppliedFrom] = useState('');
 const [appliedTo, setAppliedTo] = useState('');

 const [page, setPage] = useState(0);
 const [total, setTotal] = useState(0);

 const load = useCallback(() => {
  setLoading(true);
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  if (appliedQuery) params.set('q', appliedQuery);
  if (appliedFrom) params.set('period_from', appliedFrom);
  if (appliedTo) params.set('period_to', appliedTo);
  fetch(`/market/transactions/mine?${params.toString()}`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (j.success) {
     setRows(j.data?.rows ?? []);
     setFamilyMap(j.data?.familyMap ?? {});
     setTotal(j.data?.total ?? 0);
    } else {
     setRows([]);
     setFamilyMap({});
     setTotal(0);
    }
   })
   .finally(() => setLoading(false));
 }, [page, appliedQuery, appliedFrom, appliedTo]);

 useEffect(() => {
  load();
 }, [load]);

 useEffect(() => {
  fetch('/auth/me', { credentials: 'include' })
   .then(r => r.json())
   .then(j => setMe(j?.data?.moPeerHash ?? null));
 }, []);

 const applyFilters = () => {
  setAppliedQuery(queryDraft.trim());
  setAppliedFrom(periodFromDraft);
  setAppliedTo(periodToDraft);
  setPage(0);
 };

 const resetFilters = () => {
  setQueryDraft('');
  setPeriodFromDraft('');
  setPeriodToDraft('');
  setAppliedQuery('');
  setAppliedFrom('');
  setAppliedTo('');
  setPage(0);
 };

 const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
 const hasFilter = !!(appliedQuery || appliedFrom || appliedTo);

 const callAction = async (hash: string, path: 'confirm' | 'dispute') => {
  if (path === 'dispute' && !confirm('분쟁을 제기하시겠습니까?')) return;
  const r = await fetch(`/market/transactions/${hash}/${path}`, {
   method: 'POST', credentials: 'include',
  });
  const j = await r.json();
  if (!r.ok || !j.success) {
   alert(j.error || `${path} 실패`);
   return;
  }
  load();
 };

 return (
  <div>
   <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">거래 장부</h1>

   {/* 필터 바 */}
   <div className="bg-gray-50 border border-gray-200 p-3 mb-4">
    <div className="flex flex-wrap gap-2 items-end text-xs">
     <div className="flex flex-col">
      <label className="text-gray-600 mb-1">상대 / 모패밀리 검색</label>
      <input
       type="text"
       value={queryDraft}
       onChange={(e) => setQueryDraft(e.target.value)}
       onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
       placeholder="회원 이름·이메일 또는 모패밀리 이름"
       className="border border-gray-300 px-2 py-1 w-64"
      />
     </div>
     <div className="flex flex-col">
      <label className="text-gray-600 mb-1">기간 시작 이후</label>
      <input
       type="date"
       value={periodFromDraft}
       onChange={(e) => setPeriodFromDraft(e.target.value)}
       className="border border-gray-300 px-2 py-1"
      />
     </div>
     <div className="flex flex-col">
      <label className="text-gray-600 mb-1">기간 종료 이전</label>
      <input
       type="date"
       value={periodToDraft}
       onChange={(e) => setPeriodToDraft(e.target.value)}
       className="border border-gray-300 px-2 py-1"
      />
     </div>
     <button
      onClick={applyFilters}
      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white"
     >검색</button>
     {hasFilter && (
      <button
       onClick={resetFilters}
       className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700"
      >초기화</button>
     )}
     <div className="ml-auto text-gray-500">
      총 <strong className="text-gray-800">{total}</strong>건
      {hasFilter && <span className="ml-1 text-gray-400">(필터 적용)</span>}
     </div>
    </div>
   </div>

   {/* 거래 유형 설명 범례 */}
   <details className="mb-3 text-xs bg-gray-50 border border-gray-200 rounded">
    <summary className="cursor-pointer px-3 py-2 text-gray-700 select-none">거래 유형(Type) 설명</summary>
    <div className="px-3 pb-3 space-y-1.5 text-gray-600">
     <p><strong className="text-gray-800">Type 1 — 광고 받음 (미디어 측):</strong> 스크린을 운영하는 미디어사가 "광고를 받습니다" 자리를 열고, 광고주가 그 자리에 영상을 넣어 송출하는 거래. (미디어사 관점에서 광고를 받음)</p>
     <p><strong className="text-gray-800">Type 2 — 광고 보냄 (광고주 측):</strong> 광고주가 자기 영상을 송출할 스크린(미디어)을 찾아 제안해 성사된 거래. (광고주 관점에서 광고를 보냄)</p>
     <p><strong className="text-gray-800">Type 3 — 바터 (1:1 슬롯 교환):</strong> 두 모패밀리가 대금 없이 서로의 슬롯을 1:1로 교환해 상호 송출하는 거래. (돈이 오가지 않음)</p>
     <p className="text-gray-400 pt-1">※ Type 1·2 는 대금이 오가며, 누가 지급/수령인지는 각 거래의 "지급 측 / 수령 측" 으로 표시됩니다.</p>
    </div>
   </details>

   {loading && <div className="text-gray-500 text-sm py-8 text-center">로딩 중...</div>}
   {!loading && rows.length === 0 && (
    <div className="text-gray-400 text-sm py-12 text-center">
     {hasFilter ? '검색 조건에 맞는 거래가 없습니다.' : '거래가 없습니다.'}
    </div>
   )}
   <div className="space-y-3">
    {rows.map(t => {
     const isPayer = me === t.payer_mopeer;
     const isPayee = me === t.payee_mopeer;
     const myConfirmed = isPayer ? t.payer_confirmed : isPayee ? t.payee_confirmed : false;
     const oppConfirmed = isPayer ? t.payee_confirmed : isPayee ? t.payer_confirmed : false;
     const showActions = (isPayer || isPayee) && (t.status === 'active' || t.status === 'both_confirmed');
     const isBarter = (t.closure_reason ?? '').startsWith('barter:');
     const ctx = t.proposal_context || {};
     const proposalType = ctx.proposal_type;

     // 모패밀리 매핑 — Type 별로 의미가 다름
     // Type 1 (미디어가 광고 받기): media_family_hash = 미디어, advertiser_family_hash = 광고주 영상 모패밀리
     // Type 2 (광고주가 미디어 찾기): recipient_family_hash = 미디어, advertiser_family_hash = 광고주 영상 모패밀리
     // Type 3 (바터): sender_barter / recipient_barter 양쪽 바터 모패밀리
     const mediaFamily = familyMap[ctx.media_family_hash || ctx.recipient_family_hash || t.mo_family_hash || ''];
     const advertiserFamily = familyMap[ctx.advertiser_family_hash || ''];
     const senderBarter = familyMap[ctx.sender_barter_family_hash || ''];
     const recipientBarter = familyMap[ctx.recipient_barter_family_hash || ''];

     // 명세서는 정산 완료(settled_at) 후에만 발급 — 미정산 클릭은 안내로 막는다.
     const invoiceable = !!t.settled_at;
     const openInvoice = () => {
      if (invoiceable) {
       window.open(`/market/transactions/${t.transaction_hash}/invoice.pdf`, '_blank', 'noopener');
      } else if (isBarter) {
       alert('바터(1:1 슬롯 교환) 거래는 대금 정산이 없어 명세서가 발급되지 않습니다.');
      } else {
       alert('아직 정산되지 않은 거래입니다. 정산이 완료되면 명세서(인쇄용 PDF)가 발급됩니다.');
      }
     };

     return (
      <div
       key={t.transaction_hash}
       onClick={openInvoice}
       // 키보드 접근성 — 행 자체가 포커스됐을 때만 활성화(내부 버튼 keydown 버블링 중복 방지).
       onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
         e.preventDefault();
         openInvoice();
        }
       }}
       role="button"
       tabIndex={0}
       aria-disabled={!invoiceable}
       title={invoiceable ? '클릭하면 명세서(인쇄용 PDF)가 열립니다' : isBarter ? '바터 거래는 대금 정산/명세서가 없습니다' : '정산 완료 후 명세서가 발급됩니다'}
       className={`bg-white border border-gray-200 p-3 transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-400 ${invoiceable ? 'cursor-pointer hover:shadow-md hover:border-blue-300' : 'cursor-default'}`}
      >
       <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-sm font-medium flex items-center gap-2">
         <span title={
          proposalType === 1 ? '미디어사가 광고를 받아 자기 스크린에 송출 (대금 발생)'
           : proposalType === 2 ? '광고주가 영상을 미디어 스크린에 보내 송출 (대금 발생)'
           : proposalType === 3 ? '두 모패밀리가 대금 없이 슬롯을 1:1 교환'
           : '거래'
         }>
          {proposalType === 1 ? 'Type 1 — 광고 받음 (미디어 측)'
           : proposalType === 2 ? 'Type 2 — 광고 보냄 (광고주 측)'
           : proposalType === 3 ? 'Type 3 — 바터 (1:1 슬롯 교환)'
           : '거래'}
         </span>
         {isBarter && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5">바터</span>}
        </div>
        <div className="flex items-center gap-2">
         <span className="text-[10px] px-1.5 py-0.5 bg-gray-100">{STATUS_LABEL[t.status] || t.status}</span>
         <span className={`text-[10px] whitespace-nowrap ${invoiceable ? 'text-blue-600' : isBarter ? 'text-purple-500' : 'text-gray-400'}`}>
          {invoiceable ? '📄 명세서 ▸' : isBarter ? '정산 없음 (바터)' : '정산 후 발급'}
         </span>
        </div>
       </div>

       {/* 거래 당사자 — 회원 + 모패밀리 */}
       {isBarter ? (
        // 바터: 양측 owner + 양측 바터 모패밀리
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
         <div className="space-y-2">
          <PeerCell
           hash={t.payer_mopeer}
           name={t.payer_name}
           email={t.payer_email}
           profileImage={t.payer_profile_image}
           roleLabel="A 측 회원"
           isMe={isPayer}
          />
          <FamilyCell family={senderBarter} fallbackHash={ctx.sender_barter_family_hash || null} roleLabel="A 측 바터 모패밀리" />
         </div>
         <div className="space-y-2">
          <PeerCell
           hash={t.payee_mopeer}
           name={t.payee_name}
           email={t.payee_email}
           profileImage={t.payee_profile_image}
           roleLabel="B 측 회원"
           isMe={isPayee}
          />
          <FamilyCell family={recipientBarter} fallbackHash={ctx.recipient_barter_family_hash || null} roleLabel="B 측 바터 모패밀리" />
         </div>
        </div>
       ) : (
        // 일반 거래: 광고주 / 미디어
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
         <div className="space-y-2">
          <PeerCell
           hash={t.payer_mopeer}
           name={t.payer_name}
           email={t.payer_email}
           profileImage={t.payer_profile_image}
           roleLabel="지급 측"
           isMe={isPayer}
          />
          {advertiserFamily && (
           <FamilyCell family={advertiserFamily} fallbackHash={ctx.advertiser_family_hash || null} roleLabel="광고주 모패밀리" />
          )}
         </div>
         <div className="space-y-2">
          <PeerCell
           hash={t.payee_mopeer}
           name={t.payee_name}
           email={t.payee_email}
           profileImage={t.payee_profile_image}
           roleLabel="수령 측"
           isMe={isPayee}
          />
          <FamilyCell family={mediaFamily} fallbackHash={t.mo_family_hash} roleLabel="미디어 모패밀리" />
         </div>
        </div>
       )}

       {/* 광고 영상 + 캠페인 */}
       {(t.file_filename || t.campaign_name) && (
        <div className="text-xs text-gray-600 space-y-0.5 mb-2">
         {t.file_filename && <div>광고 영상: <strong>{t.file_filename}</strong></div>}
         {t.campaign_name && <div>캠페인: {t.campaign_name}</div>}
        </div>
       )}

       <div className="text-xs text-gray-600 space-y-0.5">
        <div>기간: {String(t.period_start).slice(0, 10)} ~ {String(t.period_end).slice(0, 10)}</div>
        <div>단가: {formatWon(t.unit_price)}/회{SHOW_FEE && ` · 수수료: ${(Number(t.commission_rate) * 100).toFixed(1)}%`}</div>
        {t.impressions !== null && (
         <>
          <div>누적 노출: {formatNumber(t.impressions)}회</div>
          <div>총액: {formatWon(t.gross_amount ?? 0)}{SHOW_FEE && ` · 수수료: ${formatWon(t.fee_amount ?? 0)} · 수령: ${formatWon(t.net_amount ?? 0)}`}</div>
         </>
        )}
        <div className="text-[10px] text-gray-400 font-mono">거래 ID: {t.transaction_hash.slice(0, 20)}…</div>
       </div>

       {showActions && (
        <div className="border-t border-gray-100 pt-2 mt-2 flex flex-wrap gap-2 items-center">
         <span className="text-xs text-gray-500">
          내 확인: {myConfirmed ? '✓' : '미확인'} · 상대 확인: {oppConfirmed ? '✓' : '미확인'}
         </span>
         {!myConfirmed && (
          <button
           onClick={(e) => { e.stopPropagation(); callAction(t.transaction_hash, 'confirm'); }}
           className="text-xs px-2 py-0.5 bg-blue-500 hover:bg-blue-600 text-white"
          >{isPayer ? '지급 완료 체크' : '수령 완료 체크'}</button>
         )}
         <button
          onClick={(e) => { e.stopPropagation(); callAction(t.transaction_hash, 'dispute'); }}
          className="text-xs px-2 py-0.5 bg-red-500 hover:bg-red-600 text-white"
         >분쟁 제기</button>
        </div>
       )}
       {t.settled_at && (
        <div className="text-[10px] text-gray-400 mt-1 flex items-center justify-between">
         <span>정산: {formatDateTime(t.settled_at)}</span>
         <a
          href={`/market/transactions/${t.transaction_hash}/invoice.pdf`}
          target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-blue-600 hover:underline text-xs"
         >📄 명세서 PDF</a>
        </div>
       )}
      </div>
     );
    })}
   </div>

   {/* 페이지네이션 */}
   {total > PAGE_SIZE && (
    <div className="flex items-center justify-center gap-2 mt-6 text-sm">
     <button
      onClick={() => setPage(p => Math.max(0, p - 1))}
      disabled={page === 0 || loading}
      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
     >이전</button>
     <span className="text-gray-600">
      {page + 1} / {totalPages}
     </span>
     <button
      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
      disabled={page >= totalPages - 1 || loading}
      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
     >다음</button>
    </div>
   )}
  </div>
 );
};

export default Transactions;
